import { CircleAlert, CircleCheck, CloudOff, LoaderCircle, LogIn, LogOut, RefreshCw, Smartphone, UserRound } from 'lucide-react';
import { useAuth } from '../../auth/authStore.ts';
import { useOriginCheck } from '../../auth/serverOrigin.ts';
import { useSync } from '../../sync/syncClient.ts';
import { useUi } from '../../App.tsx';
import { Button } from '../../ui/kit.tsx';
import { displayName, syncStatus, type StatusIconKind, type Tone } from './status.ts';
import { tr } from '../../i18n/i18n.ts';

const TONE_CLS: Record<Tone, string> = { ok: 'text-ok', warn: 'text-warn', risk: 'text-risk', neutral: 'text-muted' };

/** Ikonen följer statusens art (inte texten), så att den blir rätt på alla språk. Färg bär aldrig betydelsen ensam. */
export function StatusIcon({ tone, icon }: { tone: Tone; icon: StatusIconKind; label?: string }) {
  const size = 14;
  if (icon === 'busy') return <LoaderCircle size={size} className="shrink-0 motion-safe:animate-spin" aria-hidden />;
  if (icon === 'offline') return <CloudOff size={size} className="shrink-0" aria-hidden />;
  if (icon === 'ok' || tone === 'ok') return <CircleCheck size={size} className="shrink-0" aria-hidden />;
  if (icon === 'alert' || tone === 'risk' || tone === 'warn') return <CircleAlert size={size} className="shrink-0" aria-hidden />;
  return <RefreshCw size={size} className="shrink-0" aria-hidden />;
}

/** Kompakt kontoblock längst ned i sidomenyn (dator) och överst i Inställningar. */
export function AccountBlock({ variant = 'sidebar', onAction }: { variant?: 'sidebar' | 'settings' | 'menu'; onAction?: () => void }) {
  const a = useAuth();
  const sync = useSync();
  const raw = useUi();
  // I mobilmenyn stängs menyn först, så att kontodialogen aldrig hamnar bakom den.
  const ui = onAction
    ? { ...raw, openAuth: (...args: Parameters<typeof raw.openAuth>) => (onAction(), raw.openAuth(...args)), openAccount: () => (onAction(), raw.openAccount()), startLogout: (o?: Parameters<typeof raw.startLogout>[0]) => (onAction(), raw.startLogout(o)) }
    : raw;
  const st = a.state;
  const origin = useOriginCheck(st.status === 'guest');
  const box = variant === 'settings' ? 'rounded-[var(--radius-panel)] border border-border bg-panel p-5' : 'rounded-xl border border-border bg-bg/60 p-3';

  if (st.status === 'guest') {
    return (
      <section aria-labelledby={`acct-${variant}`} className={box}>
        <h2 id={`acct-${variant}`} className="flex items-center gap-2 text-sm font-semibold">
          <Smartphone size={16} aria-hidden /> {tr('Endast på den här enheten', 'Only on this device')}
        </h2>
        {st.locked && (
          <p className="mt-1 text-xs text-warn" role="status">
            {tr(`Kontot ${st.locked.email ?? ''} visas inte: "Kom ihåg mig" var avstängt och servern kan inte nås just nu.`, `The account ${st.locked.email ?? ''} is not shown: "Remember me" was off and the server cannot be reached right now.`)}
          </p>
        )}
        {a.pendingRevocations > 0 && (
          <p className="mt-1 text-xs text-warn" role="status">
            {tr('Utloggad på den här enheten. Servern har ännu inte bekräftat utloggningen.', 'Signed out on this device. The server has not yet confirmed the sign-out.')}
          </p>
        )}
        {origin.status === 'mismatch' ? (
          <p className="mt-1 text-xs text-warn" role="status">
            {tr('Konton fungerar inte på den här adressen. Fullständig app: ', 'Accounts do not work at this address. Full app: ')}<a className="font-semibold underline" href={origin.config.publicOrigin + '/'}>{origin.config.publicOrigin.replace(/^https?:\/\//, '')}</a>
          </p>
        ) : (
          <p className="mt-1 text-xs text-muted">{tr('Gästläge: kalendern finns bara här. Logga in för att synka mellan dina enheter.', 'Guest mode: the calendar is only here. Sign in to sync between your devices.')}</p>
        )}
        <Button variant="primary" className="mt-2 w-full" icon={<LogIn size={16} aria-hidden />} onClick={() => ui.openAuth('login')}>
          {tr('Logga in', 'Sign in')}
        </Button>
        <button type="button" className="mt-1 min-h-11 w-full rounded-lg text-sm font-medium text-accent hover:bg-raised" onClick={() => ui.openAuth('register')}>
          {tr('Skapa konto', 'Create account')}
        </button>
      </section>
    );
  }

  const p = st.profile;
  const name = displayName(p.name, p.email);
  const s = syncStatus(st, sync);
  return (
    <section aria-labelledby={`acct-${variant}`} className={box}>
      <h2 id={`acct-${variant}`} className="sr-only">
        {tr('Konto', 'Account')}
      </h2>
      <p className="flex items-center gap-2 font-semibold">
        <UserRound size={16} className="shrink-0 text-accent" aria-hidden />
        <span className="truncate" title={name}>
          {name}
        </span>
      </p>
      {p.email && (
        <p className="truncate text-xs text-muted" title={p.email}>
          {p.email}
        </p>
      )}
      <p className={`mt-1.5 flex items-center gap-1.5 text-xs ${TONE_CLS[s.tone]}`} role="status" aria-live="polite">
        <StatusIcon tone={s.tone} icon={s.icon} />
        <span>{s.label}</span>
      </p>
      {st.status === 'expired' && (
        <>
          <p className="mt-1 text-xs text-risk">{tr('Sessionen har gått ut. Logga in igen för att synka.', 'The session has expired. Sign in again to sync.')}</p>
          <Button variant="primary" className="mt-2 w-full" icon={<LogIn size={16} aria-hidden />} onClick={() => ui.openAuth('login', p.email)}>
            {tr('Logga in igen', 'Sign in again')}
          </Button>
        </>
      )}
      {/* Radbryts inte i texten; vid större textstorlek hamnar knapparna under varandra i stället. */}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button className="flex-1 whitespace-nowrap px-2" onClick={ui.openAccount}>
          {tr('Konto', 'Account')}
        </Button>
        <Button className="flex-1 whitespace-nowrap px-2" icon={<LogOut size={16} aria-hidden />} onClick={() => ui.startLogout()} disabled={a.switching}>
          {tr('Logga ut', 'Sign out')}
        </Button>
      </div>
    </section>
  );
}

/** Mobil: global kontoåtgärd i sidhuvudet på alla huvudvyer. */
export function AccountHeaderButton() {
  const a = useAuth();
  const ui = useUi();
  if (a.state.status === 'guest') {
    return (
      <Button variant="primary" className="px-3" icon={<LogIn size={16} aria-hidden />} onClick={() => ui.openAuth('login')}>
        {tr('Logga in', 'Sign in')}
      </Button>
    );
  }
  const expired = a.state.status === 'expired';
  return (
    <Button className="px-3" icon={<UserRound size={16} aria-hidden />} onClick={ui.openAccount} aria-label={expired ? tr('Konto – sessionen har gått ut', 'Account – session expired') : tr('Konto', 'Account')}>
      {tr('Konto', 'Account')}
      {expired && <CircleAlert size={14} className="text-risk" aria-hidden />}
    </Button>
  );
}

/** Tydlig rad överst när något om kontot kräver uppmärksamhet. */
export function AccountStatusBanner() {
  const a = useAuth();
  const ui = useUi();
  const st = a.state;
  if (st.status === 'expired') {
    return (
      <div className="flex flex-wrap items-center gap-3 border-b border-risk/60 bg-risk-bg px-4 py-2 text-sm" role="status">
        <span className="flex-1">{tr('Sessionen har gått ut. Logga in igen för att synka. Dina ändringar finns kvar på den här enheten.', 'The session has expired. Sign in again to sync. Your changes are still on this device.')}</span>
        <Button onClick={() => ui.openAuth('login', st.profile.email)}>{tr('Logga in igen', 'Sign in again')}</Button>
      </div>
    );
  }
  if (st.status === 'guest' && st.locked) {
    return (
      <div className="border-b border-warn/60 bg-warn-bg px-4 py-2 text-sm" role="status">
        {tr('Du ser gästläget. Kontot visas igen när du loggar in ("Kom ihåg mig" var avstängt och servern kan inte nås just nu).', 'You are seeing guest mode. The account is shown again when you sign in ("Remember me" was off and the server cannot be reached right now).')}
      </div>
    );
  }
  if (st.status === 'guest' && a.pendingRevocations > 0) {
    return (
      <div className="border-b border-warn/60 bg-warn-bg px-4 py-2 text-sm" role="status">
        {tr('Utloggad på den här enheten. Servern har ännu inte bekräftat utloggningen – det sker automatiskt när enheten är online.', 'Signed out on this device. The server has not yet confirmed the sign-out – this happens automatically when the device is online.')}
      </div>
    );
  }
  return null;
}
